import { useQuery } from '@tanstack/react-query';
import { config } from '~/config';
import { useRequest } from '~/lib/useRequest';
import { hasKey } from '~/utils';

export type ProgramIntakeDetails = {
  id: string;
  programId: string;
  campusId: string;
  intakeTermId: string;
  programName: string;
  campusName: string;
  intakeTermName: string;
  firstDayOfClass: string;
  lastDayOfClass: string;
  applicationFee: string;
  acceptingApplicationsStartsAt: string;
  acceptingApplicationsStopsAt: string;
  deferralDeadlineAt: string;
  refundDeadlineAt: string;
  tuition: string;
  tuitionPeriod: string;
  seatsCapacity?: number;
  seatsAccepted?: number;
  isAvailable?: boolean;
  isArchived?: boolean;
};

type RawIntakeResponse = {
  data: {
    id: string;
    attributes: {
      programId: string;
      campusId: string;
      intakeTermId: string;
      programName: string;
      campusName: string;
      intakeTermName: string;
      firstDayOfClassOn: string;
      lastDayOfClassOn: string;
      acceptingAppsStartAt: string;
      acceptingAppsCloseAt: string;
      refundDeadlineAt: string;
      deferralDeadlineAt: string;
      seatsCapacity?: number;
      seatsAccepted?: number;
      isAvailable?: boolean;
      isArchived?: boolean;
      applicationFee: number | null;
      tuition?: number | null;
      tuitionPeriod?: string | null;
    };
  };
};

export function useIntakeDetails(id: string) {
  const request = useRequest();

  const { isLoading, error, data, isError } = useQuery({
    queryKey: ['programIntake', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const response = await request<RawIntakeResponse>(`${config.apiHost}/private/v1/intakes/${id}`, {
        method: 'GET',
        isExpectedResponse,
      });

      const { data } = response;
      const attrs = data.attributes;

      const intake: ProgramIntakeDetails = {
        id: data.id,
        programId: attrs.programId,
        campusId: attrs.campusId,
        intakeTermId: attrs.intakeTermId,
        programName: attrs.programName,
        campusName: attrs.campusName,
        intakeTermName: attrs.intakeTermName,
        firstDayOfClass: attrs.firstDayOfClassOn,
        lastDayOfClass: attrs.lastDayOfClassOn,
        applicationFee: attrs.applicationFee != null ? attrs.applicationFee.toString() : '',
        acceptingApplicationsStartsAt: attrs.acceptingAppsStartAt,
        acceptingApplicationsStopsAt: attrs.acceptingAppsCloseAt,
        deferralDeadlineAt: attrs.deferralDeadlineAt,
        refundDeadlineAt: attrs.refundDeadlineAt,
        tuition: attrs.tuition != null ? attrs.tuition.toString() : '',
        tuitionPeriod: attrs.tuitionPeriod ?? '',
        seatsCapacity: attrs.seatsCapacity,
        seatsAccepted: attrs.seatsAccepted,
        isAvailable: attrs.isAvailable,
        isArchived: attrs.isArchived,
      };

      return intake;
    },
  });

  return {
    intake: data,
    isLoadingIntake: isLoading,
    intakeError: error,
    isIntakeError: isError,
  };
}

function isExpectedResponse(res: unknown): res is RawIntakeResponse {
  if (!res || typeof res !== 'object' || !hasKey('data', res)) {
    return false;
  }

  const data = (res as { data?: unknown }).data;
  if (!data || typeof data !== 'object' || !hasKey('attributes', data)) {
    return false;
  }

  return true;
}
